import type { OrganisationTree } from 'api/organisations';

export const isOrganisationOwned = (org?: OrganisationTree | null) =>
  Boolean(org?.owned) || String(org?.memberRoleName || '').toLowerCase() === 'organisation owner';

export const isOrganisationAdminMember = (org?: OrganisationTree | null) =>
  /organisation admin/i.test(String(org?.memberRoleName || ''));

export const canManageOrganisation = (org?: OrganisationTree | null) =>
  Boolean(org?.canManage) || isOrganisationOwned(org)
    || /organisation (owner|admin)/i.test(String(org?.memberRoleName || ''));

/** UI access label: real org roles first; staff canManage without owner/admin role → Hencol admin. */
export type OrganisationAccessKind = 'owned' | 'orgAdmin' | 'hencolAdmin' | 'invited';

export const getOrganisationAccessKind = (org?: OrganisationTree | null): OrganisationAccessKind => {
  if (!org) {
    return 'invited';
  }
  if (isOrganisationOwned(org)) {
    return 'owned';
  }
  if (isOrganisationAdminMember(org)) {
    return 'orgAdmin';
  }
  if (canManageOrganisation(org)) {
    return 'hencolAdmin';
  }
  return 'invited';
};

export const getOwnedOrganisations = (organisations?: OrganisationTree[]) =>
  (organisations ?? []).filter((org) => isOrganisationOwned(org));

export const getInvitedOrganisations = (organisations?: OrganisationTree[]) =>
  (organisations ?? []).filter((org) => !isOrganisationOwned(org));

export const getPrimaryOrganisationId = (organisations?: OrganisationTree[]) =>
  getOwnedOrganisations(organisations)[0]?.id ?? organisations?.[0]?.id;

export const getManagedOrganisationId = (
  organisations?: OrganisationTree[],
  preferredId?: number | null,
) => {
  const managed = (organisations ?? []).filter((org) => canManageOrganisation(org));
  const owned = managed.filter((org) => isOrganisationOwned(org));

  // Prefer the user's own customer number for create — never an invited selection.
  if (preferredId && owned.some((org) => org.id === preferredId)) {
    return preferredId;
  }
  if (owned[0]?.id) {
    return owned[0].id;
  }
  if (preferredId && managed.some((org) => org.id === preferredId)) {
    return preferredId;
  }
  return managed[0]?.id ?? getPrimaryOrganisationId(organisations);
};

export const findCompanyInTree = (
  organisations: OrganisationTree[] | undefined,
  companyId: string | number | null | undefined,
) => {
  const target = String(companyId ?? '').trim();
  if (!target) {
    return null;
  }

  for (const org of organisations ?? []) {
    const company = org.companies?.find((entry) => String(entry.id) === target);
    if (company) {
      return { org, company };
    }
  }

  return null;
};

export type HubCompany = {
  orgId: number;
  customerNumber?: string;
  id: number;
  name: string;
  companyRegistration?: string;
};

export type HubFarm = {
  orgId: number;
  customerNumber?: string;
  id: number;
  farmName?: string;
  farmCode?: string;
  active?: boolean;
};

const isBlankDisplayName = (name: string | undefined | null) => {
  const trimmed = String(name ?? '').trim();
  return !trimmed || trimmed === '-';
};

export const flattenHubCompanies = (
  organisations?: OrganisationTree[],
  orgFilter?: (org: OrganisationTree) => boolean,
): HubCompany[] => {
  const items: HubCompany[] = [];

  for (const org of organisations ?? []) {
    if (orgFilter && !orgFilter(org)) {
      continue;
    }
    for (const company of org.companies ?? []) {
      items.push({
        orgId: org.id,
        customerNumber: org.customerNumber,
        id: company.id,
        name: isBlankDisplayName(company.name) ? '' : String(company.name).trim(),
        companyRegistration: company.companyRegistration?.trim() || undefined,
      });
    }
  }

  return items.sort((left, right) => left.name.localeCompare(right.name, 'sv'));
};

export const flattenHubFarms = (
  organisations?: OrganisationTree[],
  orgFilter?: (org: OrganisationTree) => boolean,
): HubFarm[] => {
  const byId = new Map<number, HubFarm>();

  for (const org of organisations ?? []) {
    if (orgFilter && !orgFilter(org)) {
      continue;
    }
    for (const farm of org.farms ?? []) {
      if (farm.active === false) {
        continue;
      }
      byId.set(farm.id, {
        orgId: org.id,
        customerNumber: org.customerNumber,
        id: farm.id,
        farmName: farm.farmName,
        farmCode: farm.farmCode,
        active: farm.active,
      });
    }

    for (const company of org.companies ?? []) {
      for (const farm of company.farms ?? []) {
        if (farm.active === false || byId.has(farm.id)) {
          continue;
        }
        byId.set(farm.id, {
          orgId: org.id,
          customerNumber: org.customerNumber,
          id: farm.id,
          farmName: farm.farmName,
          farmCode: farm.farmCode,
          active: farm.active,
        });
      }
    }
  }

  return Array.from(byId.values()).sort((left, right) =>
    (left.farmName || '').localeCompare(right.farmName || '', 'sv'),
  );
};

export const hasAnyCompaniesOrFarms = (organisations?: OrganisationTree[]) =>
  flattenHubCompanies(organisations).length > 0 || flattenHubFarms(organisations).length > 0;

export const customerNumberLabel = (org?: OrganisationTree | null) =>
  org?.customerNumber?.trim() || (org?.id ? `CUST-${String(org.id).padStart(6, '0')}` : '');
